import React, { useState, useEffect, useRef } from 'react';
import { useSignup } from './SignupProvider';

const RestaurantInfoStep: React.FC = () => {
  const { state, updateRestaurantData, setError, clearError, nextStep } = useSignup();
  const { restaurant } = state.data;
  const { errors } = state;
  const nameInputRef = useRef<HTMLInputElement>(null);

  const [formData, setFormData] = useState({
    name: restaurant?.name || '',
    address: restaurant?.address || '',
    city: restaurant?.city || '',
    state: restaurant?.state || '',
    zipCode: restaurant?.zipCode || '',
    phone: restaurant?.phone || '',
    website: restaurant?.website || '',
  });

  // Update form data when restaurant data changes
  useEffect(() => {
    if (restaurant) {
      setFormData({
        name: restaurant.name || '',
        address: restaurant.address || '',
        city: restaurant.city || '',
        state: restaurant.state || '',
        zipCode: restaurant.zipCode || '',
        phone: restaurant.phone || '',
        website: restaurant.website || '',
      });
    }
  }, [restaurant]);

  // Auto-focus on the first input field when component mounts
  useEffect(() => {
    if (nameInputRef.current) {
      nameInputRef.current.focus();
    }
  }, []);

  const formatPhoneNumber = (value: string, previousValue: string = '') => {
    // Remove all non-digits
    const phoneNumber = value.replace(/\D/g, '');
    
    // If user is deleting (new value has fewer digits than before), allow it
    const previousDigits = previousValue.replace(/\D/g, '');
    const isDeleting = phoneNumber.length < previousDigits.length;
    
    // Limit to 10 digits
    const limitedPhoneNumber = phoneNumber.slice(0, 10);
    
    // If deleting and we're at a formatting boundary, don't reformat immediately
    if (isDeleting && limitedPhoneNumber.length > 0) {
      // Allow partial formatting during deletion
      if (limitedPhoneNumber.length <= 3) {
        return limitedPhoneNumber;
      } else if (limitedPhoneNumber.length <= 6) {
        return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3)}`;
      }
    }
    
    // Format as (XXX) XXX-XXXX
    if (limitedPhoneNumber.length >= 6) {
      return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3, 6)}-${limitedPhoneNumber.slice(6, 10)}`;
    } else if (limitedPhoneNumber.length >= 3) {
      return `(${limitedPhoneNumber.slice(0, 3)}) ${limitedPhoneNumber.slice(3)}`;
    } else {
      return limitedPhoneNumber;
    }
  };

  const formatZipCode = (value: string) => {
    // Remove all non-digits
    const zipCode = value.replace(/\D/g, '');
    
    // Limit to 9 digits (5 + 4 for ZIP+4)
    const limitedZipCode = zipCode.slice(0, 9);
    
    // Format as XXXXX-XXXX if more than 5 digits
    if (limitedZipCode.length > 5) {
      return `${limitedZipCode.slice(0, 5)}-${limitedZipCode.slice(5)}`;
    } else {
      return limitedZipCode;
    }
  };

  const formatWebsiteUrl = (value: string) => {
    if (!value.trim()) return '';
    
    // Remove any existing protocol prefixes
    let cleanUrl = value.trim().replace(/^https?:\/\//, '');
    
    // Remove www. prefix
    cleanUrl = cleanUrl.replace(/^www\./, '');
    
    // Remove trailing slashes
    cleanUrl = cleanUrl.replace(/\/+$/, '');
    
    // If there's still content, add https:// prefix
    if (cleanUrl) {
      return `https://${cleanUrl}`;
    }
    
    return cleanUrl;
  };

  const handleInputChange = (field: string, value: string) => {
    let processedValue = value;
    
    // Format phone number
    if (field === 'phone') {
      processedValue = formatPhoneNumber(value, formData.phone);
    }
    
    // Format ZIP code
    if (field === 'zipCode') {
      processedValue = formatZipCode(value);
    }
    
    // Format website URL
    if (field === 'website') {
      processedValue = formatWebsiteUrl(value);
    }
    
    setFormData(prev => ({ ...prev, [field]: processedValue }));
    updateRestaurantData({ [field]: processedValue });
    
    // Clear error when user starts typing
    if (errors[field]) {
      clearError(field);
    }
  };

  const validateForm = () => {
    const newErrors: Record<string, string> = {};

    // Required fields
    if (!formData.name.trim()) {
      newErrors.name = 'Restaurant name is required';
    }
    if (!formData.address.trim()) {
      newErrors.address = 'Address is required';
    }
    if (!formData.city.trim()) {
      newErrors.city = 'City is required';
    }
    if (!formData.state.trim()) {
      newErrors.state = 'State is required';
    }
    if (!formData.zipCode.trim()) {
      newErrors.zipCode = 'ZIP code is required';
    }
    if (!formData.phone.trim()) {
      newErrors.phone = 'Restaurant phone number is required';
    }

    // Phone validation (10-digit US phone number)
    const phoneDigits = formData.phone.replace(/\D/g, '');
    if (formData.phone && phoneDigits.length !== 10) {
      newErrors.phone = 'Please enter a valid 10-digit phone number';
    }

    // ZIP code validation (5 or 9 digits)
    const zipDigits = formData.zipCode.replace(/\D/g, '');
    if (formData.zipCode && zipDigits.length !== 5 && zipDigits.length !== 9) {
      newErrors.zipCode = 'Please enter a valid ZIP code (5 or 9 digits)';
    }

    // Website validation (optional)
    if (formData.website && formData.website.trim()) {
      try {
        new URL(formData.website.startsWith('http') ? formData.website : `https://${formData.website}`);
      } catch {
        newErrors.website = 'Please enter a valid website URL';
      }
    }

    // Set errors
    Object.entries(newErrors).forEach(([field, message]) => {
      setError(field, message);
    });

    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (validateForm()) {
      nextStep();
    }
  };

  const US_STATES = [
    'AL', 'AK', 'AZ', 'AR', 'CA', 'CO', 'CT', 'DE', 'FL', 'GA',
    'HI', 'ID', 'IL', 'IN', 'IA', 'KS', 'KY', 'LA', 'ME', 'MD',
    'MA', 'MI', 'MN', 'MS', 'MO', 'MT', 'NE', 'NV', 'NH', 'NJ',
    'NM', 'NY', 'NC', 'ND', 'OH', 'OK', 'OR', 'PA', 'RI', 'SC',
    'SD', 'TN', 'TX', 'UT', 'VT', 'VA', 'WA', 'WV', 'WI', 'WY'
  ].sort();

  return (
    <div className="restaurant-info-step">
      <div className="step-header">
        <h2>Restaurant Information</h2>
        <p>Tell us about your restaurant to get started</p>
        <p className="payment-token-warning">
          Please make sure to have the payment token from TableChat before proceeding with sign up.
        </p>
      </div>

      <form className="restaurant-form" onSubmit={(e) => e.preventDefault()}>
        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="name">Restaurant Name *</label>
            <input
              ref={nameInputRef}
              type="text"
              id="name"
              value={formData.name}
              onChange={(e) => handleInputChange('name', e.target.value)}
              className={errors.name ? 'error' : ''}
              placeholder="Enter your restaurant name"
            />
            {errors.name && <span className="error-message">{errors.name}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="address">Street Address *</label>
            <input
              type="text"
              id="address"
              value={formData.address}
              onChange={(e) => handleInputChange('address', e.target.value)}
              className={errors.address ? 'error' : ''}
              placeholder="Enter your street address"
            />
            {errors.address && <span className="error-message">{errors.address}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="city">City *</label>
            <input
              type="text"
              id="city"
              value={formData.city}
              onChange={(e) => handleInputChange('city', e.target.value)}
              className={errors.city ? 'error' : ''}
              placeholder="City"
            />
            {errors.city && <span className="error-message">{errors.city}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="state">State *</label>
            <select
              id="state"
              value={formData.state}
              onChange={(e) => handleInputChange('state', e.target.value)}
              className={errors.state ? 'error' : ''}
            >
              <option value="">Select State</option>
              {US_STATES.map(state => (
                <option key={state} value={state}>{state}</option>
              ))}
            </select>
            {errors.state && <span className="error-message">{errors.state}</span>}
          </div>

          <div className="form-group">
            <label htmlFor="zipCode">ZIP Code *</label>
            <input
              type="tel"
              id="zipCode"
              value={formData.zipCode}
              onChange={(e) => handleInputChange('zipCode', e.target.value)}
              className={errors.zipCode ? 'error' : ''}
              placeholder="12345"
              inputMode="numeric"
            />
            {errors.zipCode && <span className="error-message">{errors.zipCode}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group">
            <label htmlFor="phone">Restaurant Phone Number *</label>
            <input
              type="tel"
              id="phone"
              value={formData.phone}
              onChange={(e) => handleInputChange('phone', e.target.value)}
              className={errors.phone ? 'error' : ''}
              placeholder="(555) 123-4567"
            />
            {errors.phone && <span className="error-message">{errors.phone}</span>}
          </div>
        </div>

        <div className="form-row">
          <div className="form-group full-width">
            <label htmlFor="website">Website (Optional)</label>
            <input
              type="url"
              id="website"
              value={formData.website}
              onChange={(e) => handleInputChange('website', e.target.value)}
              className={errors.website ? 'error' : ''}
              placeholder="https://yourrestaurant.com"
            />
            {errors.website && <span className="error-message">{errors.website}</span>}
          </div>
        </div>
      </form>

      <div className="step-actions">
        <button
          type="button"
          className="btn btn-primary"
          onClick={handleNext}
        >
          Next: Manager Details
        </button>
      </div>

      <style>{`
        .restaurant-info-step {
          max-width: 600px;
          margin: 0 auto;
          padding: 20px;
        }

        .step-header {
          text-align: center;
          margin-bottom: 32px;
        }

        .step-header h2 {
          color: #2196F3;
          margin-bottom: 8px;
          font-size: 2rem;
          font-weight: 600;
        }

        .step-header p {
          color: #6c757d;
          font-size: 1.1rem;
        }

        .payment-token-warning {
          color: #dc3545 !important;
          font-weight: 500;
          font-size: 0.85rem !important;
          margin-top: 8px;
        }

        .restaurant-form {
          margin-bottom: 32px;
        }

        .form-row {
          display: flex;
          gap: 16px;
          margin-bottom: 20px;
        }

        .form-group {
          flex: 1;
          display: flex;
          flex-direction: column;
        }

        .form-group.full-width {
          width: 100%;
        }

        .form-group label {
          margin-bottom: 6px;
          font-weight: 500;
          color: #495057;
        }

        .form-group input,
        .form-group select,
        .form-group textarea {
          padding: 12px;
          border: 2px solid #e9ecef;
          border-radius: 8px;
          font-size: 1rem;
          transition: border-color 0.2s;
        }

        .form-group input:focus,
        .form-group select:focus,
        .form-group textarea:focus {
          outline: none;
          border-color: #2196F3;
        }

        .form-group input.error,
        .form-group select.error,
        .form-group textarea.error {
          border-color: #dc3545;
        }

        .error-message {
          color: #dc3545;
          font-size: 0.875rem;
          margin-top: 4px;
        }

        .step-actions {
          display: flex;
          justify-content: center;
          gap: 16px;
        }

        .btn {
          padding: 12px 24px;
          border: none;
          border-radius: 8px;
          font-size: 1rem;
          font-weight: 500;
          cursor: pointer;
          transition: all 0.2s;
        }

        .btn-primary {
          background: #2196F3;
          color: white;
        }

        .btn-primary:hover {
          background: #1976D2;
          transform: translateY(-1px);
        }

        @media (max-width: 768px) {
          .form-row {
            flex-direction: column;
            gap: 12px;
          }

          .restaurant-info-step {
            padding: 16px;
          }

          .step-header h2 {
            font-size: 1.5rem;
          }
        }
      `}</style>
    </div>
  );
};

export default RestaurantInfoStep;
